import React, { useState, useEffect } from "react";
import axios from "axios";
import { apiService } from '../service/apiService';
import { exportTableToPDF } from "../utils/exportPDF";

const WestDateList = () => {
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [kitchenId, setKitchenId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [bulkAddMode, setBulkAddMode] = useState(false);
  const [projectMaster, setProjectMaster] = useState([]);
  const [siteMater, setsiteMater] = useState([]);
  const [allSites, setAllSites] = useState([]);

  useEffect(() => {
    loadProjectMaster();
    loadAllSites();
  }, []);

  /* -----------------------------
      LOAD PROJECT MASTER
  ------------------------------ */
  const loadProjectMaster = async () => {
    setLoading(true);
    try {
      const data = await apiService.getProjectMasterList();
      setProjectMaster(data);
    } catch (error) {
      console.error("Failed to load projects", error);
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------
      LOAD ALL SITES
  ------------------------------ */
  const loadAllSites = async () => {
    try {
      const data = await apiService.getAllSites();
      setAllSites(data);
    } catch (error) {
      console.error("Failed to load sites", error);
    }
  };

  const itemsPerPage = 10;

  // Filters - Multiple select for sites
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedSites, setSelectedSites] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Single form data
  const [formData, setFormData] = useState({
    project_name: "",
    breakfast: "",
    lunch: "",
    dinner: "",
    date: new Date().toISOString().split("T")[0],
    project_type: "current",
    site: ""
  });

  // Bulk add data - for multiple entries
  const [bulkFormData, setBulkFormData] = useState([
    {
      project_name: "",
      breakfast: "",
      lunch: "",
      dinner: "",
      date: new Date().toISOString().split("T")[0],
      project_type: "current",
      site: ""
    }
  ]);

  // Fetch user kitchenId
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user) return;
    if (user.role === "project" && user.user_id) {
      const projectKitchenId = localStorage.getItem("projectKichenID");
      if (projectKitchenId) setKitchenId(projectKitchenId);
    } else if (user.kitchen) {
      setKitchenId(user.kitchen);
    }
  }, []);

  // Fetch stock list
  useEffect(() => {
    if (kitchenId !== null) {
      fetchStocks();
    }
  }, [kitchenId]);

  const fetchStocks = async () => {
    try {
      const response = await axios.get(
        `https://api-ims.indevconsultancy.in/KitchenProjectList.php?kitchen_id=${kitchenId}&project_type=current`
      );
      const dataArray = Array.isArray(response.data.data)
        ? response.data.data
        : [];

      setStocks(dataArray);
      setFilteredStocks(dataArray);
    } catch (error) {
      console.error("Error fetching stocks:", error);
      setStocks([]);
      setFilteredStocks([]);
    } finally {
      setLoading(false);
    }
  };

  // PAGINATION
  const totalPages = Math.ceil(filteredStocks.length / itemsPerPage);
  const paginatedList = filteredStocks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // SINGLE FORM HANDLERS
  const handleChange = (e) => {
    if (e.target.name === 'project_name') {
      loadSiteMaster(e.target.value);
      // Reset site when project changes
      setFormData(prev => ({ ...prev, site: "" }));
    }
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // BULK FORM HANDLERS
  const handleBulkChange = (index, e) => {
    const { name, value } = e.target;
    const updatedBulkData = [...bulkFormData];
    updatedBulkData[index] = {
      ...updatedBulkData[index],
      [name]: value
    };

    if (name === 'project_name') {
      loadSiteMaster(value);
      // Reset site when project changes in bulk form
      updatedBulkData[index].site = "";
    }
    setBulkFormData(updatedBulkData);
  };

  const loadSiteMaster = async (project_id) => {
    setLoading(true);
    try {
      const data = await apiService.getSite({ project_id: project_id });
      setsiteMater(data);
    } catch (error) {
      console.error("Failed to load sites", error);
    } finally {
      setLoading(false);
    }
  };

  const addBulkRow = () => {
    setBulkFormData([
      ...bulkFormData,
      {
        project_name: "",
        breakfast: "",
        lunch: "",
        dinner: "",
        date: new Date().toISOString().split("T")[0],
        project_type: "current",
        site: ""
      }
    ]);
  };

  const removeBulkRow = (index) => {
    if (bulkFormData.length > 1) {
      const updatedBulkData = bulkFormData.filter((_, i) => i !== index);
      setBulkFormData(updatedBulkData);
    }
  };

  // RESET FORMS
  const resetForm = () => {
    setFormData({
      project_name: "",
      breakfast: "",
      lunch: "",
      dinner: "",
      date: new Date().toISOString().split("T")[0],
      project_type: "current",
      site: ""
    });
    setEditMode(false);
    setEditingId(null);
    setBulkAddMode(false);
    setBulkFormData([{
      project_name: "",
      breakfast: "",
      lunch: "",
      dinner: "",
      date: new Date().toISOString().split("T")[0],
      project_type: "current",
      site: ""
    }]);
  };

  // CHECK IF PROJECT AND SITE COMBINATION ALREADY EXISTS
  const checkProjectSiteExists = (projectName, site, date, excludeId = null) => {
    return stocks.some(stock =>
      stock.project_name === projectName &&
      stock.site === site &&
      stock.date === date &&
      stock.id !== excludeId
    );
  };

  // CHECK FOR BULK ENTRIES
  const checkBulkProjectSiteExists = (bulkData) => {
    const duplicates = [];
    const uniqueCombinations = new Set();

    bulkData.forEach((row, index) => {
      const combination = `${row.project_name}-${row.site}-${row.date}`;
      if (uniqueCombinations.has(combination)) {
        duplicates.push(index + 1);
      } else {
        uniqueCombinations.add(combination);
      }

      // Check against existing data
      if (checkProjectSiteExists(row.project_name, row.site, row.date)) {
        duplicates.push(index + 1);
      }
    });

    return duplicates;
  };

  // SINGLE FORM SUBMIT - ADD/UPDATE PROJECT
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.project_name ||
      !formData.breakfast ||
      !formData.lunch ||
      !formData.dinner ||
      !formData.date ||
      !formData.site
    ) {
      alert("Please fill all required fields");
      return;
    }

    // Get project name from project_id
    const selectedProject = projectMaster.find(p => p.project_id === formData.project_name);
    const projectName = selectedProject ? selectedProject.project_name : formData.project_name;

    // Get site name from site_id
    const selectedSite = siteMater.find(s => s.site_id === formData.site);
    const siteName = selectedSite ? selectedSite.site_name : formData.site;

    // Check if project-site combination already exists
    if (checkProjectSiteExists(projectName, siteName, formData.date, editingId)) {
      alert("This project and site combination already exists for the selected date!");
      return;
    }

    try {
      const payload = {
        ...formData,
        // project_name: projectName,
        // site: siteName,
        kitchenId,
        current_date: new Date().toISOString().split("T")[0]
      };

      let response;

      if (editMode && editingId) {
        // UPDATE EXISTING PROJECT
        payload.id = editingId;
        response = await axios.put(
          "https://api-ims.indevconsultancy.in/KitchenProject.php",
          payload
        );

        if (response.data.success) {
          alert("Project updated successfully!");
          fetchStocks();
          resetForm();
          closeModal();
        } else {
          alert(response.data.message || "Failed to update project");
        }
      } else {
        // ADD NEW PROJECT
        response = await axios.post(
          "https://api-ims.indevconsultancy.in/KitchenProject.php",
          payload
        );

        if (response.data.success) {
          alert("Project added successfully!");
          fetchStocks();
          resetForm();
          closeModal();
        } else {
          alert(response.data.message || "Failed to save project");
        }
      }

    } catch (error) {
      console.error("Error:", error);
      alert(`Failed to ${editMode ? "update" : "save"} project`);
    }
  };

  // BULK FORM SUBMIT - ADD MULTIPLE PROJECTS
  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    // Validate all rows
    const invalidRows = bulkFormData.filter(row =>
      !row.project_name || !row.breakfast || !row.lunch || !row.dinner || !row.date || !row.site
    );

    if (invalidRows.length > 0) {
      alert("Please fill all required fields in all rows");
      return;
    }

    // Check for duplicate project-site combinations in bulk data
    const duplicateRows = checkBulkProjectSiteExists(bulkFormData);
    if (duplicateRows.length > 0) {
      alert(`Duplicate project-site combinations found in rows: ${duplicateRows.join(', ')}`);
      return;
    }

    try {
      const promises = bulkFormData.map(row => {
        // Send the ids, not the names — the list query joins
        // project_master.project_id and site_master.site_id on these columns
        return axios.post("https://api-ims.indevconsultancy.in/KitchenProject.php", {
          ...row,
          kitchenId,
          current_date: new Date().toISOString().split("T")[0]
        });
      });

      const results = await Promise.allSettled(promises);

      const successful = results.filter(result =>
        result.status === 'fulfilled' && result.value.data.success
      );

      const failed = results.filter(result =>
        result.status === 'rejected' || !result.value?.data.success
      );

      if (failed.length === 0) {
        alert(`All ${successful.length} projects added successfully!`);
      } else {
        alert(`${successful.length} projects added successfully, ${failed.length} failed.`);
      }

      fetchStocks();
      resetForm();
      closeModal();

    } catch (error) {
      console.error("Error adding bulk projects:", error);
      alert("Failed to add some projects");
    }
  };



  const safeNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const sumField = (arr, field) =>
    arr.reduce((sum, item) => sum + safeNumber(item[field]), 0);

  const breakfastTotal = sumField(filteredStocks, "breakfast");
  const lunchTotal = sumField(filteredStocks, "lunch");
  const dinnerTotal = sumField(filteredStocks, "dinner");


  // CLOSE MODAL
  const closeModal = () => {
    const modal = document.getElementById("registerModal");
    const modalInstance = window.bootstrap.Modal.getInstance(modal);
    modalInstance.hide();
  };

  // EDIT PROJECT
  const handleEdit = (project) => {
    // Find project_id from project_name
    const projectObj = projectMaster.find(p => p.project_name === project.project_name);
    const projectId = projectObj ? projectObj.project_id : project.project_name;

    // Find site_id from site_name
    const siteObj = siteMater.find(s => s.site_name === project.site);
    const siteId = siteObj ? siteObj.site_id : project.site;

    setFormData({
      project_name: projectId || "",
      breakfast: project.breakfast || "",
      lunch: project.lunch || "",
      dinner: project.dinner || "",
      date: project.date || new Date().toISOString().split("T")[0],
      project_type: project.project_type || "current",
      site: siteId || ""
    });
    setEditMode(true);
    setEditingId(project.id);
    setBulkAddMode(false);

    // Load sites for the selected project
    if (projectId) {
      loadSiteMaster(projectId);
    }

    // Open modal
    const modal = new window.bootstrap.Modal(document.getElementById('registerModal'));
    modal.show();
  };

  // DELETE PROJECT
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) {
      return;
    }

    try {
      const response = await axios.delete(
        "https://api-ims.indevconsultancy.in/KitchenProject.php",
        { data: { id } }
      );

      if (response.data.success) {
        alert("Project deleted successfully!");
        fetchStocks();
      } else {
        alert(response.data.message || "Failed to delete project");
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Failed to delete project");
    }
  };

  // BULK DELETE
  const handleBulkDelete = async (ids) => {
    if (!window.confirm(`Are you sure you want to delete ${ids.length} projects?`)) {
      return;
    }

    try {
      const promises = ids.map(id =>
        axios.delete("https://api-ims.indevconsultancy.in/KitchenProject.php", { data: { id } })
      );

      const results = await Promise.allSettled(promises);

      const successful = results.filter(result =>
        result.status === 'fulfilled' && result.value.data.success
      );

      alert(`${successful.length} projects deleted successfully!`);
      fetchStocks();

    } catch (error) {
      console.error("Error deleting projects:", error);
      alert("Failed to delete some projects");
    }
  };

  // TOGGLE BULK ADD MODE
  const toggleBulkAddMode = () => {
    setBulkAddMode(!bulkAddMode);
    setEditMode(false);
    setEditingId(null);
  };

  // 🔍 APPLY FILTERS - WITH MULTIPLE SELECT FOR SITES
  const applyFilters = () => {
    let data = stocks;

    // Filter by selected project
    if (selectedProject) {
      data = data.filter((item) => item.project_name === selectedProject);
    }

    // Filter by selected sites (multiple)
    if (selectedSites.length > 0) {
      data = data.filter((item) => selectedSites.includes(item.site));
    }

    // Filter by date range
    if (startDate) {
      data = data.filter((item) => item.date >= startDate);
    }

    if (endDate) {
      data = data.filter((item) => item.date <= endDate);
    }

    setFilteredStocks(data);
    setCurrentPage(1);
  };

  // 🔄 RESET FILTERS
  const resetFilters = () => {
    setSelectedProject("");
    setSelectedSites([]);
    setStartDate("");
    setEndDate("");
    setFilteredStocks(stocks);
    setCurrentPage(1);
  };

  // HANDLE MULTIPLE SITE SELECTION
  const handleSiteSelection = (e) => {
    const options = e.target.options;
    const selectedValues = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    setSelectedSites(selectedValues);
  };

  // Get unique projects and sites for filters
  const uniqueProjects = [...new Set(stocks.map(item => item.project_name))].filter(Boolean);
  const uniqueSites = [...new Set(stocks.map(item => item.site))].filter(Boolean);

  const exportCSV = () => {
    if (!selectedProject) {
      alert("Please select a project first!");
      return;
    }

    const url = `https://api-ims.indevconsultancy.in/report/report-prject.php?project_id=${selectedProject}`;

    setLoading(true); // show loader
    window.location.href = url;

    setTimeout(() => {
      setLoading(false); // hide loader
    }, 10000); // 10 seconds
  };

  const exportPDF = () => {
    if (!filteredStocks || filteredStocks.length === 0) return alert("No data to export.");
    const headers = ["Project Name", "Breakfast", "Lunch", "Dinner", "Date", "Site"];
    const rows = filteredStocks.map((item) => [
      item.project_name,
      item.breakfast,
      item.lunch,
      item.dinner,
      item.date,
      item.site_name || "",
    ]);
    exportTableToPDF({ title: "West Date Report", headers, rows, filename: "west_date_report.pdf", filterInfo: `Total: ${rows.length}` });
  };

  // const exportCSV = () => {
  //   const headers = ["Project Name", "Breakfast", "Lunch", "Dinner", "Date", "Site"];

  //   const rows = filteredStocks.map((item) => [
  //     item.project_name,
  //     item.breakfast,
  //     item.lunch,
  //     item.dinner,
  //     item.date,
  //     item.site,
  //   ]);

  //   const csvContent =
  //     "data:text/csv;charset=utf-8," +
  //     [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

  //   const url = encodeURI(csvContent);
  //   const link = document.createElement("a");

  //   link.setAttribute("href", url);
  //   link.setAttribute("download", "project_list.csv");
  //   document.body.appendChild(link);
  //   link.click();
  // };

  return (
    <>
      <div className="content">
        <div className="container-fluid">

          {/* FILTER SECTION WITH MULTIPLE SELECT OPTIONS */}
          <div className="card mb-3">
            <div className="card-header border-info">
              <form>
                <div className="row g-3">
                  {/* Project Name Filter - Select Option */}
                  <div className="col-lg-3">
                    <label className="form-label small">Project Name</label>
                    <select
                      className="form-control"
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                    >
                      <option value="">All Projects</option>
                      {uniqueProjects.map((project, index) => (
                        <option key={index} value={project}>
                          {project}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Site Filter - Multiple Select Option */}
                  <div className="col-lg-3">
                    <label className="form-label small">Sites </label>
                    <select

                      className="form-control"
                      value={selectedSites}
                      onChange={handleSiteSelection}
                    >
                      {uniqueSites.map((site, index) => (
                        <option key={index} value={site}>
                          {site}
                        </option>
                      ))}
                    </select>

                  </div>

                  {/* Date Filters */}
                  <div className="col-lg-2">
                    <label className="form-label small">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="col-lg-2">
                    <label className="form-label small">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>

                  <div className="col-lg-1 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={applyFilters}
                    >
                      Search
                    </button>
                  </div>

                  <div className="col-lg-1 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-dark w-100"
                      onClick={resetFilters}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* TABLE SECTION */}
          <div className="card">
            <div className="card-body">

              <div className="row g-3 mb-4">

                {/* Breakfast */}
                <div className="col-md-4 col-sm-6">
                  <div className="p-3 rounded shadow-sm bg-white d-flex align-items-center border">
                    <div className="bg-warning text-white rounded-circle d-flex justify-content-center align-items-center" style={{ width: "45px", height: "45px" }}>
                      <i className="fas fa-coffee"></i>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 text-muted">Breakfast Total</h5>
                      <h4 className="fw-bold">{breakfastTotal}</h4>
                    </div>
                  </div>
                </div>

                {/* Lunch */}
                <div className="col-md-4 col-sm-6">
                  <div className="p-3 rounded shadow-sm bg-white d-flex align-items-center border">
                    <div className="bg-success text-white rounded-circle d-flex justify-content-center align-items-center" style={{ width: "45px", height: "45px" }}>
                      <i className="fas fa-utensils"></i>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 text-muted">Lunch Total</h5>
                      <h4 className="fw-bold">{lunchTotal}</h4>
                    </div>
                  </div>
                </div>

                {/* Dinner */}
                <div className="col-md-4 col-sm-6">
                  <div className="p-3 rounded shadow-sm bg-white d-flex align-items-center border">
                    <div className="bg-danger text-white rounded-circle d-flex justify-content-center align-items-center" style={{ width: "45px", height: "45px" }}>
                      <i className="fas fa-hamburger"></i>
                    </div>
                    <div className="ms-3">
                      <h5 className="mb-0 text-muted">Dinner Total</h5>
                      <h4 className="fw-bold">{dinnerTotal}</h4>
                    </div>
                  </div>
                </div>

              </div>

              {/* TOP STATS + BUTTONS */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">

                {/* Stats */}
                <div className="d-flex flex-wrap gap-4">
                  <h6 className="fw-bold mb-0">Total Project(s): {filteredStocks.length}</h6>
                </div>

                {/* Buttons */}
                <div>

                  <button className="btn btn-success me-2" onClick={exportCSV}>
                    Export CSV
                  </button>
                  <button className="btn btn-danger me-2" onClick={exportPDF}>
                    Export PDF
                  </button>

                  <button
                    className="btn btn-info me-2"
                    data-bs-toggle="modal"
                    data-bs-target="#registerModal"
                    onClick={toggleBulkAddMode}
                  >
                    Bulk Add
                  </button>

                  <button
                    className="btn btn-primary"
                    data-bs-toggle="modal"
                    data-bs-target="#registerModal"
                    onClick={resetForm}
                  >
                    Add Project
                  </button>
                </div>
              </div>

              {/* TABLE */}
              <div className="table-responsive scrollbar">
                <table className="table text-center table-bordered table-striped">

                  <thead className="bg-primary text-white">
                    <tr>
                      <th>S.No.</th>
                      <th>Project Name</th>
                      <th>Breakfast</th>
                      <th>Lunch</th>
                      <th>Dinner</th>
                      <th>Date</th>
                      <th>Site</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr><td colSpan="8">Loading...</td></tr>
                    ) : paginatedList.length === 0 ? (
                      <tr><td colSpan="8">No records found.</td></tr>
                    ) : (
                      paginatedList.map((item, i) => (
                        <tr key={i}>
                          <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                          <td>{item.project_name}</td>
                          <td>{item.breakfast}</td>
                          <td>{item.lunch}</td>
                          <td>{item.dinner}</td>
                          <td>{new Date(item.date).toLocaleDateString("en-GB").replace(/\//g, "-")}</td>
                          <td>{item.site}</td>

                          <td>
                            {user.role === "project" && user.user_id ? (
                              <div className="btn-group btn-group-sm">
                                <button className="btn btn-warning" onClick={() => handleEdit(item)} title="Edit">
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button className="btn btn-danger" onClick={() => handleDelete(item.id)} title="Delete">
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            ) : <span>—</span>}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>

                </table>
              </div>

              {/* PAGINATION */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                <span>Page {currentPage} of {totalPages}</span>

                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* MODAL */}
      <div
        className="modal fade"
        id="registerModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form onSubmit={bulkAddMode ? handleBulkSubmit : handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editMode ? "Edit Project" : bulkAddMode ? "Bulk Add Projects" : "Add Project"}
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={resetForm}></button>
              </div>

              <div className="modal-body">
                {bulkAddMode ? (
                  // BULK ADD FORM
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Add Multiple Projects</h6>
                      <div>
                        <button
                          type="button"
                          className="btn btn-success btn-sm me-2"
                          onClick={addBulkRow}
                        >
                          Add Row
                        </button>
                        <span className="badge bg-primary">
                          {bulkFormData.length} row(s)
                        </span>
                      </div>
                    </div>

                    {bulkFormData.map((row, index) => (
                      <div key={index} className="border p-3 mb-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">Project #{index + 1}</h6>
                          {bulkFormData.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removeBulkRow(index)}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="row g-3">
                          <div className="col-md-12">
                            <label className="form-label">Date *</label>
                            <input
                              type="date"
                              className="form-control"
                              name="date"
                              value={row.date}
                              onChange={(e) => handleBulkChange(index, e)}
                              required
                            />
                          </div>

                          <div className="col-md-6">
                            <label className="form-label">Project Name *</label>
                            <select
                              className="form-control"
                              name="project_name"
                              value={row.project_name}
                              onChange={(e) => handleBulkChange(index, e)}
                              required
                            >
                              <option value="">Select Project</option>
                              {projectMaster.map((p) => (
                                <option key={p.project_id} value={p.project_id}>
                                  {p.project_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label">Site *</label>
                            <select
                              className="form-control"
                              name="site"
                              value={row.site}
                              onChange={(e) => handleBulkChange(index, e)}
                              required
                              disabled={!row.project_name}
                            >
                              <option value="">Select Site</option>
                              {siteMater.map((p) => (
                                <option key={p.site_id} value={p.site_id}>
                                  {p.site_name}
                                </option>
                              ))}
                            </select>
                            {!row.project_name && (
                              <small className="text-muted">Please select a project first</small>
                            )}
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Breakfast *</label>
                            <input
                              type="number"
                              className="form-control"
                              name="breakfast"
                              value={row.breakfast}
                              onChange={(e) => handleBulkChange(index, e)}
                              required
                              min="0"
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Lunch *</label>
                            <input
                              type="number"
                              className="form-control"
                              name="lunch"
                              value={row.lunch}
                              onChange={(e) => handleBulkChange(index, e)}
                              required
                              min="0"
                            />
                          </div>

                          <div className="col-md-4">
                            <label className="form-label">Dinner *</label>
                            <input
                              type="number"
                              className="form-control"
                              name="dinner"
                              value={row.dinner}
                              onChange={(e) => handleBulkChange(index, e)}
                              required
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // SINGLE FORM
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Project Name *</label>
                      <input type="text"
                        className="form-control"
                        name="project_name"
                        value={formData.project_name}
                        onChange={handleChange}
                        required
                      >

                      </input>
                    </div>

                    <div className="col-md-6 d-none" >
                      <label className="form-label">Site *</label>
                      <select
                        className="form-control"
                        name="site"
                        value={formData.site}
                        onChange={handleChange}
                        required
                        disabled={!formData.project_name}
                      >
                        <option value="">Select Site</option>
                        {siteMater.map((p) => (
                          <option key={p.site_id} value={p.site_id}>
                            {p.site_name}
                          </option>
                        ))}
                      </select>
                      {!formData.project_name && (
                        <small className="text-muted">Please select a project first</small>
                      )}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Breakfast *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="breakfast"
                        value={formData.breakfast}
                        onChange={handleChange}
                        required
                        min="0"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Lunch *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="lunch"
                        value={formData.lunch}
                        onChange={handleChange}
                        required
                        min="0"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Dinner *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="dinner"
                        value={formData.dinner}
                        onChange={handleChange}
                        required
                        min="0"
                      />
                    </div>

                    <input
                      type="hidden"
                      name="project_type"
                      value="current"
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  Close
                </button>
                <button className="btn btn-primary" type="submit">
                  {editMode ? "Update Project" : bulkAddMode ? "Save All Projects" : "Save Project"}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default WestDateList;